/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */
import './style/header.scss';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {getMode} from '@dvrd/dvr-controls';
import userCircle from '@iconify/icons-fa-solid/user-circle';
import signOut from '@iconify/icons-fa/sign-out';
import menuIcon from '@iconify/icons-mdi/menu';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import {AppContext} from '../misc/appContext';
import {useMobileNav} from '../misc/mobileNavContext';
import {BolmateTooltip} from '../controls';
import ContextAccountController from '../account/select/contextAccountController';
import FeedbackButton from '../feedback/feedbackButton';
import FeedbackDialogController from '../feedback/feedbackDialogController';
import SystemMessagesController from '../systemMessage/systemMessagesController';
import Image from '../misc/image';
import HeaderCrumbs from './crumbs/headerCrumbs';
import PortalSelectController from '../controls/portalSelect/portalSelectController';
import {useBreakpoint} from '../../util/useBreakpoint';
import {getCurrentLanguage, setUserLanguage} from '../../i18n/languageService';
import {useTranslation} from 'react-i18next';
import {PortalSelectOption} from '../../util/types';

interface Props {
    onClickLogo: MouseEventHandler;
    onClickLogout: MouseEventHandler;
    menuWidth: number;
}

export default function Header(props: Props) {
    const {t, i18n} = useTranslation();
    const context = useContext(AppContext);
    const {user} = context.userContext;
    const {account} = context.accountContext;
    const {onClickLogo, onClickLogout, menuWidth} = props;
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    // PWA-03: derive the toggle's language from the live i18n language on every
    // render (useTranslation re-renders on 'languageChanged'), mirroring the mobile
    // bottom-nav. Holding it in local state left the toggle stuck on the pre-login
    // default when app.tsx switched the language right after first login.
    const language = getCurrentLanguage();
    const [headerHeight, setHeaderHeight] = useState(0);
    // Mirrors app.tsx: on the mobile layout the BottomNavBar replaces the sidebar and the header
    // collapses to just the logo. Account select, language and sign-out live in the "Meer" menu;
    // the env indicator is desktop-only. The trial counter/upgrade CTA is rendered by
    // app.tsx (renderTrialBanner), so it shows on every layout.
    const bp = useBreakpoint();
    const isMobileLayout = bp.isMobileLayout;
    const mobileNav = useMobileNav();
    const [showEnv, setShowEnv] = useState(true);
    const headerRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef(
        new ResizeObserver(() => {
            determineHeaderHeight();
        })
    );
    const languageItems: Array<PortalSelectOption> = [
        {
            value: 'nl',
            label: (
                <span className='header-language-option'>
                    <span className='fi fi-nl' />
                    {t('settings.language.nl', 'Nederlands')}
                </span>
            )
        },
        {
            value: 'en',
            label: (
                <span className='header-language-option'>
                    <span className='fi fi-gb' />
                    {t('settings.language.en', 'Engels')}
                </span>
            )
        }
    ];

    function onDoubleClickEnv() {
        setShowEnv(false);
    }

    function determineHeaderHeight() {
        const header = headerRef.current;
        if (!header) return;
        const subWarning = header.querySelector('div.sub-warning'),
            height = header.getBoundingClientRect().height + (subWarning?.getBoundingClientRect().height ?? 0);
        setHeaderHeight(height);
        // Lets fixed elements outside the header (e.g. the bottom-nav menu drawer) anchor below it.
        document.documentElement.style.setProperty('--bolmate-header-height', `${height}px`);
    }

    function attachListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.observe(header);
    }

    function removeListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.unobserve(header);
    }

    function renderEnv() {
        if (!showEnv) return null;
        const mode = getMode();
        let env: string | null = null;
        if (mode === 'accept') env = 'Acceptatie omgeving';
        else if (['test', 'sub', 'comet', 'orbit'].includes(mode)) env = 'Test omgeving';
        if (env)
            return (
                <span className='env' onDoubleClick={onDoubleClickEnv}>
                    {env}
                </span>
            );
        return null;
    }

    function onClickFeedback() {
        setFeedbackOpen(true);
    }

    function onCloseFeedback() {
        setFeedbackOpen(false);
    }

    function onChangeLanguage(nextLanguage: string | null) {
        if (nextLanguage !== 'nl' && nextLanguage !== 'en') return;
        // No local setState — i18n.changeLanguage re-renders the header and
        // `language` is re-derived from i18n above (PWA-03).
        void i18n.changeLanguage(nextLanguage);
        void setUserLanguage(user, nextLanguage);
    }

    function renderRightCluster() {
        return (
            <div className='header-actions'>
                {renderEnv()}
                <PortalSelectController
                    value={language}
                    onChange={onChangeLanguage}
                    items={languageItems}
                    label={t('settings.language.label', 'Taal')}
                    className='header-language-select account-field'
                />
                {user !== null && (
                    <div className='account-field'>
                        <ContextAccountController />
                    </div>
                )}
                {user !== null && account !== null && (
                    <BolmateTooltip text={t('feedback.tooltip', 'Feedback or report a problem')}>
                        <span className='feedback-btn-anchor'>
                            <FeedbackButton onClick={onClickFeedback} />
                        </span>
                    </BolmateTooltip>
                )}
                {user !== null && (
                    <div className='account-field user-info'>
                        <Icon icon={userCircle} className='user-icon' />
                        <label>{user.fullName}</label>
                        <div className='sign-out-btn' onClick={onClickLogout}>
                            <Icon icon={signOut} />
                        </div>
                    </div>
                )}
            </div>
        );
    }

    useEffect(() => {
        window.setTimeout(() => {
            attachListener();
            determineHeaderHeight();
        });
        return function () {
            removeListener();
        };
    }, []);

    return (
        <>
            <div id='invisible-header' style={{height: `${headerHeight}px`}} />
            <div className='bolmate-header' ref={headerRef}>
                <SystemMessagesController />
                <div className='header-inner'>
                    {isMobileLayout ? (
                        <>
                            <div className='header-mobile-slot'>
                                {mobileNav.available && (
                                    <div
                                        className={classNames('mobile-menu-toggle', mobileNav.menuOpen && 'active')}
                                        onClick={mobileNav.toggleMenu}
                                        role='button'
                                        aria-label={t('menu.title', 'Menu')}
                                        aria-expanded={mobileNav.menuOpen}
                                    >
                                        <Icon icon={menuIcon} />
                                    </div>
                                )}
                            </div>
                            <div className='logo-container'>
                                <Image
                                    name='logo'
                                    className='logo'
                                    onClick={onClickLogo}
                                    onLoad={determineHeaderHeight}
                                />
                            </div>
                            {/* Empty counterpart of the menu button, so the logo stays centred. */}
                            <div className='header-mobile-slot' />
                        </>
                    ) : (
                        <>
                            <div className='header-content left'>
                                <div style={{width: `${menuWidth}px`}} className='logo-container'>
                                    <Image
                                        name='logo'
                                        className='logo'
                                        onClick={onClickLogo}
                                        onLoad={determineHeaderHeight}
                                    />
                                </div>
                                <HeaderCrumbs />
                            </div>
                            <div className='header-content right'>{renderRightCluster()}</div>
                        </>
                    )}
                </div>
            </div>
            <FeedbackDialogController active={feedbackOpen} onClose={onCloseFeedback} />
        </>
    );
}
